import { ReactNode, Dispatch, SetStateAction, useMemo } from 'react';
import {
  Button,
  Card,
  CardHeader,
  Checkbox,
  Divider,
  Grid,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  TextField,
  Box,
} from '@mui/material';
import { useDebounce } from '../utils/useDebounce';

function not(a: readonly string[], b: readonly string[]) {
  return a.filter((value) => b.indexOf(value) === -1);
}

function intersection(a: readonly string[], b: readonly string[]) {
  return a.filter((value) => b.indexOf(value) !== -1);
}

function union(a: readonly string[], b: readonly string[]) {
  return [...a, ...not(b, a)];
}

interface ITransferListProps {
  right: readonly string[];
  setRight: Dispatch<SetStateAction<readonly string[]>>;
  left: readonly string[];
  setLeft: Dispatch<SetStateAction<readonly string[]>>;
  checked: readonly string[];
  setChecked: Dispatch<SetStateAction<readonly string[]>>;
  leftFilter: string;
  setLeftFilter: Dispatch<SetStateAction<string>>;
  rightFilter: string;
  setRightFilter: Dispatch<SetStateAction<string>>;
}

export const TransferList = ({
  right,
  setRight,
  left,
  setLeft,
  checked,
  setChecked,
  leftFilter,
  setLeftFilter,
  rightFilter,
  setRightFilter,
}: ITransferListProps) => {
  const debouncedLeftFilter = useDebounce(leftFilter, 300);
  const debouncedRightFilter = useDebounce(rightFilter, 300);

  const leftChecked = intersection(checked, left);
  const rightChecked = intersection(checked, right);

  const filteredLeft = useMemo(
    () =>
      left.filter((item) =>
        item.toLowerCase().includes(debouncedLeftFilter.toLowerCase())
      ),
    [left, debouncedLeftFilter]
  );

  const filteredRight = useMemo(
    () =>
      right.filter((item) =>
        item.toLowerCase().includes(debouncedRightFilter.toLowerCase())
      ),
    [right, debouncedRightFilter]
  );

  const handleToggle = (value: string) => () => {
    const currentIndex = checked.indexOf(value);
    const newChecked = [...checked];

    if (currentIndex === -1) {
      newChecked.push(value);
    } else {
      newChecked.splice(currentIndex, 1);
    }

    setChecked(newChecked);
  };

  const numberOfChecked = (items: readonly string[]) =>
    intersection(checked, items).length;

  const handleToggleAll = (items: readonly string[]) => () => {
    if (numberOfChecked(items) === items.length) {
      setChecked(not(checked, items));
    } else {
      setChecked(union(checked, items));
    }
  };

  const handleCheckedRight = () => {
    setRight(right.concat(leftChecked));
    setLeft(not(left, leftChecked));
    setChecked(not(checked, leftChecked));
  };

  const handleCheckedLeft = () => {
    setLeft(left.concat(rightChecked));
    setRight(not(right, rightChecked));
    setChecked(not(checked, rightChecked));
  };

  const customList = (
    title: ReactNode,
    items: readonly string[],
    filter: string,
    setFilter: (val: string) => void
  ) => {
    return (
      <Card>
        <CardHeader
          sx={{ px: 2, py: 1 }}
          avatar={
            <Checkbox
              onClick={handleToggleAll(items)}
              checked={
                numberOfChecked(items) === items.length && items.length !== 0
              }
              indeterminate={
                numberOfChecked(items) !== items.length &&
                numberOfChecked(items) !== 0
              }
              disabled={items.length === 0}
              inputProps={{
                'aria-label': 'all items selected',
              }}
            />
          }
          title={title}
          subheader={`${numberOfChecked(items)}/${items.length} seleccionados`}
        />
        <Divider />
        <Box px={2} py={1}>
          <TextField
            fullWidth
            size="small"
            placeholder="Buscar..."
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
        </Box>
        <List
          sx={{
            width: 500,
            height: 500,
            bgcolor: 'background.paper',
            overflow: 'auto',
          }}
          dense
          component="div"
          role="list"
        >
          {items.map((value: string) => {
            const labelId = `transfer-list-all-item-${value}-label`;

            return (
              <ListItemButton
                key={value}
                role="listitem"
                onClick={handleToggle(value)}
              >
                <ListItemIcon>
                  <Checkbox
                    checked={checked.indexOf(value) !== -1}
                    tabIndex={-1}
                    disableRipple
                    inputProps={{
                      'aria-labelledby': labelId,
                    }}
                  />
                </ListItemIcon>
                <ListItemText id={labelId} primary={value} />
              </ListItemButton>
            );
          })}
        </List>
      </Card>
    );
  };

  return (
    <Grid container spacing={2} justifyContent="center" alignItems="center">
      <Grid>
        {customList('No asignados', filteredLeft, leftFilter, setLeftFilter)}
      </Grid>
      <Grid>
        <Grid container direction="column" alignItems="center">
          <Button
            sx={{ my: 0.5 }}
            variant="outlined"
            size="small"
            onClick={handleCheckedRight}
            disabled={leftChecked.length === 0}
            aria-label="move selected right"
          >
            &gt;
          </Button>
          <Button
            sx={{ my: 0.5 }}
            variant="outlined"
            size="small"
            onClick={handleCheckedLeft}
            disabled={rightChecked.length === 0}
            aria-label="move selected left"
          >
            &lt;
          </Button>
        </Grid>
      </Grid>
      <Grid>
        {customList('Asignados', filteredRight, rightFilter, setRightFilter)}
      </Grid>
    </Grid>
  );
};
